import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilterCircleXmark, faFilter, faArrowLeft, faUsers, faTachometer, faEye, faPencil, faTrashCan, faPlus, faTable, faArrowRight, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { getReportListAPI, deleteReportAPI } from "../../API/Report";
import { useRecoilState } from 'recoil';
import { DateTime } from "luxon";

import FormSelectField from "../Reusable/FormSelectField";
import { USER_REPORT_SORT_OPTIONS } from "../../Constants/FieldOptions";
import FormErrorBox from "../Reusable/FormErrorBox";
import PageLoadingContent from "../Reusable/PageLoadingContent";
import {
    topAlertMessageState, topAlertStatusState,
    userReportsShowFilterState, userReportsSortedByState, userDetailState
} from "../../AppState";


function UserDetailForReportList() {
    ////
    //// URL Parameters.
    ////

    const { uid } = useParams()

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);
    const [showFilter, setShowFilter] = useRecoilState(userReportsShowFilterState);
    const [sortByValue, setSortByValue] = useRecoilState(userReportsSortedByState);     // Sorting
    const [user] = useRecoilState(userDetailState);

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [selectedReportForDeletion, setSelectedReportForDeletion] = useState("");
    const [isFetching, setFetching] = useState(false);
    const [listData, setListData] = useState("");                // List Data + Pagination
    const [pageSize, setPageSize] = useState(10);                // Pagination
    const [previousCursors, setPreviousCursors] = useState([]);  // Pagination
    const [nextCursor, setNextCursor] = useState("");            // Pagination
    const [currentCursor, setCurrentCursor] = useState("");      // Pagination

    ////
    //// API.
    ////

    function onUserDetailForReportListSuccess(response){
        console.log("onUserDetailForReportListSuccess: Starting...");
        setListData(response);
        if (response.hasNextPage) {
            setNextCursor(response.nextCursor); // For pagination purposes.
        }
    }

    function onUserDetailForReportListError(apiErr) {
        console.log("onUserDetailForReportListError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onUserDetailForReportListDone() {
        console.log("onUserDetailForReportListDone: Starting...");
        setFetching(false);
    }

    function onReportDeleteSuccess(response){
        console.log("onReportDeleteSuccess: Starting..."); // For debugging purposes only.

        // Update notification.
        setTopAlertStatus("success");
        setTopAlertMessage("Report deleted");
        setTimeout(() => {
            console.log("onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Fetch again an updated list.
        defaultFetchList();
    }

    function onReportDeleteError(apiErr) {
        console.log("onReportDeleteError: Starting..."); // For debugging purposes only.
        setErrors(apiErr);

        // Update notification.
        setTopAlertStatus("danger");
        setTopAlertMessage("Failed deleting");
        setTimeout(() => {
            console.log("onReportDeleteError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onReportDeleteDone() {
        console.log("onReportDeleteDone: Starting...");
        setFetching(false);
    }

    ////
    //// Event handling.
    ////

    const onClearFilterClick = (e) => {
        setShowFilter(false);
        setSortByValue("created,DESC");
    }

    // Convienience function.
    const defaultFetchList = () => {
        fetchList(currentCursor, pageSize, sortByValue, uid);
    }

    const fetchList = (cur, limit, sbv, uid) => {
        setFetching(true);
        setErrors({});

        let params = new Map();
        params.set("page_size", limit);     // Pagination

        // DEVELOPERS NOTE: Our `sortByValue` is string with the sort field
        // and sort order combined with a comma seperation. Therefore we
        // need to split as follows.
        if (sbv !== undefined && sbv !== null && sbv !== "") {
            const sortArray = sbv.split(",");
            params.set("sort_field", sortArray[0]); // Sort (1 of 2)
            params.set("sort_order", sortArray[1]); // Sort (2 of 2)
        }

        if (cur !== "") {
            params.set("cursor", cur); // Pagination
        }

        if (uid !== "") { // Pagination
            params.set("user_id", uid);
        }

        getReportListAPI(
            params,
            onUserDetailForReportListSuccess,
            onUserDetailForReportListError,
            onUserDetailForReportListDone
        );
    }

    const onSelectReportForDeletion = (e, report) => {
        console.log("onSelectReportForDeletion", report);
        setSelectedReportForDeletion(report);
    }

    const onDeselectReportForDeletion = (e) => {
        console.log("onDeselectReportForDeletion");
        setSelectedReportForDeletion("");
    }

    const onDeleteConfirmButtonClick = (e) => {
        console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

        deleteReportAPI(
            selectedReportForDeletion.id,
            onReportDeleteSuccess,
            onReportDeleteError,
            onReportDeleteDone
        );
        setSelectedReportForDeletion("");
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            fetchList(currentCursor, pageSize, sortByValue, uid);
        }

        return () => { mounted = false; }
    }, [currentCursor, pageSize, sortByValue, uid]);

    ////
    //// Component rendering.
    ////

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/users" aria-current="page"><FontAwesomeIcon className="fas" icon={faUsers} />&nbsp;Users</Link></li>
                            <li class="is-active"><Link to={`/user`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Detail (Reports)</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/users`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Users</Link></li>
                        </ul>
                    </nav>

                    {/* Content */}
                    <nav class="box">
                        <div class={`modal ${selectedReportForDeletion ? 'is-active' : ''}`}>
                            <div class="modal-background"></div>
                            <div class="modal-card">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">Are you sure?</p>
                                    <button class="delete" aria-label="close" onClick={onDeselectReportForDeletion}></button>
                                </header>
                                <section class="modal-card-body">
                                    You are about to delete this report and all the data inside of it. This action is cannot be undone. Are you sure you would like to continue?
                                </section>
                                <footer class="modal-card-foot">
                                    <button class="button is-success" onClick={onDeleteConfirmButtonClick}>Confirm</button>
                                    <button class="button" onClick={onDeselectReportForDeletion}>Cancel</button>
                                </footer>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <h1 class="title is-4"><FontAwesomeIcon className="fas" icon={faUsers} />&nbsp;User - {user.firstName} {user.lastName}</h1>
                            </div>
                            {!isFetching && <div class="column has-text-right">
                                <button class="button is-small is-info is-fullwidth-mobile" onClick={defaultFetchList}>
                                    <FontAwesomeIcon className="mdi" icon={faRefresh} /><span className="is-hidden-desktop is-hidden-tablet">&nbsp;Refresh</span>
                                </button>
                                &nbsp;
                                <button onClick={(e,i)=>{setShowFilter(!showFilter)}} class="button is-small is-link is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faFilter} />&nbsp;Filter
                                </button>
                            </div>}
                        </div>

                        {/* Tab Navigation */}
                        <div class="tabs is-medium">
                            <ul>
                                <li>
                                    <Link class="is-active" to={`/user/${uid}`}>Detail</Link>
                                </li>
                                <li>
                                    <Link to={`/user/${uid}/sessions`}>Sessions</Link>
                                </li>
                                <li>
                                    <Link><strong>Reports</strong></Link>
                                </li>
                            </ul>
                        </div>

                        {/* Filter Box */}
                        {showFilter &&
                            <div class="has-background-white-bis" style={{borderRadius:"15px", padding:"20px"}}>
                                {/* Filter Title + Clear Button */}
                                <div class="columns">
                                    <div class="column is-half">
                                        <strong><u><FontAwesomeIcon className="mdi" icon={faFilter} />&nbsp;Filter</u></strong>
                                    </div>
                                    <div class="column is-half has-text-right">
                                        <Link onClick={onClearFilterClick}><FontAwesomeIcon className="mdi" icon={faFilterCircleXmark} />&nbsp;Clear Filter</Link>
                                    </div>
                                </div>

                                {/* Filter Options */}
                                <div class="columns">
                                    <div class="column">
                                        <FormSelectField
                                            label="Sort by"
                                            name="sortByValue"
                                            placeholder="Pick sorting"
                                            selectedValue={sortByValue}
                                            helpText=""
                                            onChange={(e)=>setSortByValue(e.target.value)}
                                            options={USER_REPORT_SORT_OPTIONS}
                                            isRequired={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        }

                        {isFetching
                            ?
                            <>
                                <PageLoadingContent displayMessage={"Please wait"} />
                            </>
                            :
                            <>
                                <FormErrorBox errors={errors} />
                                {listData && listData.results && (listData.results.length > 0 || previousCursors.length > 0)
                                    ?
                                    <div class="container">
                                        <div class="b-table">
                                            <div class="table-wrapper has-mobile-cards">
                                                <table class="table is-fullwidth is-striped is-hoverable is-fullwidth">
                                                    <thead>
                                                        <tr>
                                                            <th>ID</th>
                                                            <th>Created</th>
                                                            <th></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {listData && listData.results && listData.results.map(function(datum, i){
                                                            return <tr>
                                                                <td data-label="ID">{datum.id}</td>
                                                                <td data-label="Name">{DateTime.fromSeconds(datum.created).toLocaleString(DateTime.DATETIME_MED)}</td>
                                                                <td class="is-actions-cell">
                                                                    <div class="buttons is-right">
                                                                        <Link to={`/user/${datum.userId}/report/${datum.id}`} class="button is-small is-primary" type="button">
                                                                            <FontAwesomeIcon className="mdi" icon={faEye} />&nbsp;View
                                                                        </Link>
                                                                         {/*
                                                                        <Link to={`/user/${datum.userId}/report/${datum.id}/edit`} class="button is-small is-warning" type="button">
                                                                            <FontAwesomeIcon className="mdi" icon={faPencil} />&nbsp;Edit
                                                                        </Link>
                                                                        <button onClick={(e, ses) => onSelectReportForDeletion(e, datum)} class="button is-small is-danger" type="button">
                                                                            <FontAwesomeIcon className="mdi" icon={faTrashCan} />&nbsp;Delete
                                                                        </button>
                                                                        */}
                                                                    </div>
                                                                </td>
                                                            </tr>;
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    :
                                    <div class="hero-body">
                                      <p class="title">
                                          <FontAwesomeIcon className="fas" icon={faTable} />&nbsp;No data
                                      </p>
                                      <p class="subtitle">
                                          There has been no data generated by Rhea Core. Please check back later.
                                      </p>
                                    </div>
                                }
                            </>
                        }

                        <div class="columns pt-5">
                            <div class="column is-half">
                                <Link to="/users" class="button is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Users</Link>
                            </div>
                            <div class="column is-half has-text-right">
                            </div>
                        </div>
                    </nav>
                </section>
            </div>
        </>
    );
}

export default UserDetailForReportList;
